import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Loader } from "../../../revvComponents";
import { amplitudeLogEvent } from "../../../utils/amplitudeUtils";

// Action Imports
import {
  getDocMetadata,
  detectDocMetadata,
  setCommonHash,
  setMetalensActiveStatus,
} from "../../../actions/MetalensActions/metalensActions";
import { updateToolPaneData } from "../../../actions/QuotationActions/QuotationActions";

// Service Imports
import {
  downloadDocMetadataService,
  downloadSharedDocMetadataService,
} from "../../../actions/MetalensActions/metalensServices";

import { EVENT_NAMES } from "../../../constants/events";
import { emptyImage } from "../../../constants/images";

// Utils
import { downloadObject } from "../../../utils/metalensUtils";

import AddMetalensTag from "./AddMetalensTag";
import MetalensTag from "./MetalensTag";

const MetadataSection = ({
  metadata,
  docID,
  getDocMetadataAction,
  // toggleDropdown,
  // metalensRef,
  detectDocMetadataAction,
  currentHash,
  previousHash,
  setCommonHashAction,
  setMetalensActiveStatusAction,
  // updateToolPaneDataAction,
  // bgChangeWrapperVisible,
  lastActive,
  match: { params },
}) => {
  const downloadRef = useRef(null);
  const metadataSectionRef = useRef(null);
  const [addActive, setAddActive] = useState(false);

  const [reAnalyseLoader, setReAnalyseLoader] = useState(false);
  const [downloadDropdown, setDownloadDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  let sharingHash = null;
  const { sharingHash: newSharingHash } = params;
  if (newSharingHash) {
    sharingHash = newSharingHash;
  }

  useEffect(() => {
    setIsLoading(true);
    getDocMetadataAction(docID, sharingHash)
      .then(res => {
        if (!(res && res.length)) {
          detectDocMetadataAction(docID, sharingHash)
            .then(() => {
              setIsLoading(false);
            })
            .catch(() => {
              setIsLoading(false);
            });
        } else {
          setIsLoading(false);
        }
      })
      .catch(err => {
        setIsLoading(false);
        console.error(err);
      });
    setMetalensActiveStatusAction(true);
    return () => {
      setMetalensActiveStatusAction(false);
    };
  }, [
    detectDocMetadataAction,
    docID,
    getDocMetadataAction,
    setMetalensActiveStatusAction,
    sharingHash,
  ]);

  useEffect(() => {
    const _handleClickOutsideDownload = event => {
      if (
        downloadRef &&
        downloadRef.current &&
        !downloadRef.current.contains(event.target) &&
        !(
          event.target.dataset &&
          event.target.dataset.id === "metalens-btn-download"
        )
      ) {
        setDownloadDropdown(false);
      }
    };
    const metadataSectionReference = metadataSectionRef;
    if (
      downloadDropdown &&
      metadataSectionReference &&
      metadataSectionReference.current
    ) {
      metadataSectionReference.current.addEventListener(
        "click",
        _handleClickOutsideDownload,
      );
    }
    return () => {
      if (metadataSectionReference && metadataSectionReference.current) {
        metadataSectionReference.current.removeEventListener(
          "click",
          _handleClickOutsideDownload,
        );
      }
    };
  }, [downloadDropdown]);

  const _handleAddMetaTag = () => {
    if (addActive) {
      return;
    }
    setAddActive(true);
  };

  const _closeAddMetaTag = () => {
    setAddActive(false);
  };

  const _downloadJSON = (format = "json") => {
    let downloadService = downloadDocMetadataService;
    if (sharingHash) {
      downloadService = downloadSharedDocMetadataService;
    }
    downloadService(docID, format, sharingHash)
      .then(res => {
        downloadObject(res, "revv-metadata", format);
        setDownloadDropdown(false);
      })
      .catch(err => {
        console.error(err);
        setDownloadDropdown(false);
      });
    // Amplitude
    amplitudeLogEvent(EVENT_NAMES.METALENS_DOWNLOAD, {
      Type: format,
    });
  };

  const _metaTagsRender = () => {
    if (isLoading) {
      return (
        <div className="metalens-is-loading-container">
          <Loader
            label="Loading metadata"
            className="metalens-is-loading"
            fullScreen={false}
          />
        </div>
      );
    }

    if (metadata && metadata.length) {
      return metadata.map(item => (
        //
        <MetalensTag
          key={`${item.id}metalens-tag`}
          title={item.meta_key}
          tag={item.text}
          occurances={item.mentions ? item.mentions.length : 1}
          id={item.id}
          type={item.entity_type}
          titleSuggestions={item.key_suggestions}
          mentions={item.mentions}
          docID={docID}
          sharingHash={sharingHash}
          isEdited={item.value_edited}
        />
      ));
    }
    return (
      <div className="metalens-tags-empty-image-container">
        <img
          src={emptyImage}
          alt="empty-metatags"
          className="metalens-tags-empty-image"
        />
        This document does not have any metadata. You can add custom metatags
        using the “Add metatag” button.
      </div>
    );
  };

  return (
    <div
      ref={metadataSectionRef}
      data-belongsto="metalens-dropdown"
      className="metadata-section-wrapper">
      <div className="metalens-tags-head">
        <div className="metalens-tags-head-title">
          <span>Metadata in this document</span>{" "}
          <span className="metalens-tags-head-total">
            {(metadata && metadata.length) || 0}
          </span>
        </div>
        {metadata && metadata.length ? (
          <div className="metalens-tags-head-info">
            You can customise the tags using the <br />
            <span aria-label="revv-ai-bolt" role="img">
              ⚡
            </span>{" "}
            Revv AI suggestions. Also,{" "}
            <b>
              <i>download</i>
            </b>{" "}
            data in standard JSON format for use with other business apps.
          </div>
        ) : null}
      </div>
      <div className="metalens-action">
        <Button
          data-test="metalens-btn-add-tag"
          className="metalens-btn-add-tag "
          onClick={_handleAddMetaTag}
          id="metalens-action-add">
          <FontAwesomeIcon icon={["fas", "plus"]} className="" />
          &nbsp; Add Metadata
        </Button>
        <Button
          data-test="metalens-btn-download"
          className="metalens-btn-download"
          data-id="metalens-btn-download"
          onClick={() => setDownloadDropdown(!downloadDropdown)}
          id="metalens-action-download">
          <FontAwesomeIcon icon={["fas", "download"]} className="" />
          &nbsp; Download &nbsp;
          <i
            data-id="metalens-btn-download"
            className={
              downloadDropdown ? "metalens-download-dropdown-open" : ""
            }>
            <FontAwesomeIcon
              data-id="metalens-btn-download"
              icon={["far", "angle-down"]}
            />
          </i>
        </Button>
        {downloadDropdown && (
          <div ref={downloadRef} className="metalens-download-dropdown">
            <ul className="metalens-download-dropdown-list">
              <li>
                <div
                  data-test="metalens-btn-download-csv"
                  onKeyDown={() => {}}
                  tabIndex="0"
                  role="button"
                  className="metalens-download-dropdown-list-item"
                  onClick={() => _downloadJSON("csv")}>
                  CSV
                </div>
              </li>
              <li>
                <div
                  data-test="metalens-btn-download-csv"
                  onKeyDown={() => {}}
                  tabIndex="0"
                  role="button"
                  className="metalens-download-dropdown-list-item"
                  onClick={() => _downloadJSON("json")}>
                  JSON
                </div>
              </li>
            </ul>
          </div>
        )}
      </div>
      <div className="metalens-tags">
        <div className="metalens-tags-head">
          {currentHash !== previousHash && (
            <div className="metalens-detect-action">
              {reAnalyseLoader && (
                <Loader
                  label="Re-analyzing this document"
                  className="metalens-detect-loading"
                  fullScreen={false}
                />
              )}
              <div className="metalens-detect-info">
                <span className="metalens-detect-info-icon">
                  <FontAwesomeIcon
                    icon={["far", "engine-warning"]}
                    className=""
                  />
                </span>{" "}
                Changes detected. Re-analysing the doc might lose your
                customisation.
              </div>
              <Button
                data-test="metalens-btn-reanalyze"
                className="metalens-btn-add-tag metalens-detect-action-btn"
                onClick={() => {
                  setReAnalyseLoader(true);
                  detectDocMetadataAction(docID, sharingHash)
                    .then(() => {
                      setCommonHashAction();
                      setReAnalyseLoader(false);
                    })
                    .catch(() => {
                      setReAnalyseLoader(false);
                    });
                }}
                id="metalens-action-add">
                <FontAwesomeIcon icon={["far", "sync-alt"]} className="" />
                &nbsp; Re-Analyze this doc
              </Button>
            </div>
          )}
          {addActive && (
            <AddMetalensTag
              sharingHash={sharingHash}
              handleClose={_closeAddMetaTag}
            />
          )}
          <div className="metalens-tags-body">{_metaTagsRender()}</div>
        </div>
      </div>
      <div data-belongsto="metalens-dropdown" className="metalens-foot">
        {lastActive
          ? `Last updated ${Math.abs(
              Math.floor((new Date(lastActive) - Date.now()) / 60000),
            )} minutes ago`
          : ""}
      </div>
    </div>
  );
};

const mapStateToProps = ({
  MetalensStore: { metadata, currentHash, previousHash, lastActive },
  DocumentStore: { document: { id: docID } = {} } = {},
}) => ({
  metadata,
  docID,
  currentHash,
  previousHash,
  lastActive,
});

const mapDispatchToProps = {
  getDocMetadataAction: getDocMetadata,
  detectDocMetadataAction: detectDocMetadata,
  setCommonHashAction: setCommonHash,
  setMetalensActiveStatusAction: setMetalensActiveStatus,
  updateToolPaneDataAction: updateToolPaneData,
};
export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(MetadataSection),
);
